import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Home,
  User, 
  Phone, 
  Mail, 
  MapPin,
  MessageSquare,
  Plus,
  Package,
  DollarSign,
  TrendingUp,
  FileText,
  Star,
  Link2,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function SellerProfile() {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  
  const [seller, setSeller] = useState(null);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [applications, setApplications] = useState([]);
  const [activity, setActivity] = useState([]);
  const [messages, setMessages] = useState({ sms: [], emails: [] });
  const [agreements, setAgreements] = useState([]);
  const [sendingAgreementId, setSendingAgreementId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const loadSellerData = useCallback(async () => {
    try {
      setLoading(true);
      const [sellerRes, statsRes, itemsRes, appsRes, activityRes, messagesRes, agreementsRes, commissionsRes] = await Promise.all([
        axios.get(`${API}/sellers/${sellerId}`, { withCredentials: true }),
        axios.get(`${API}/sellers/${sellerId}/stats`, { withCredentials: true }),
        axios.get(`${API}/sellers/${sellerId}/items`, { withCredentials: true }),
        axios.get(`${API}/sellers/${sellerId}/applications`, { withCredentials: true }),
        axios.get(`${API}/sellers/${sellerId}/activity`, { withCredentials: true }),
        axios.get(`${API}/sellers/${sellerId}/messages`, { withCredentials: true }),
        axios.get(`${API}/sellers/${sellerId}/agreements`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/sellers/${sellerId}/commissions`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);

      setSeller(sellerRes.data);
      setStats(statsRes.data);
      setItems(itemsRes.data || []);
      setApplications(appsRes.data || []);
      setActivity(activityRes.data || []);
      setMessages(messagesRes.data || { sms: [], emails: [] });
      setAgreements(agreementsRes.data || []);
      setCommissions(commissionsRes.data);
    } catch (error) {
      toast.error('Failed to load seller profile');
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  const sendSellerAgreement = async (ag, via) => {
    try {
      setSendingAgreementId(ag.id);
      await axios.post(`${API}/applications/${ag.application_id}/agreement/send`, { via }, { withCredentials: true });
      toast.success(via === 'sms' ? 'Agreement link sent via SMS' : 'Agreement sent via email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send agreement');
    } finally {
      setSendingAgreementId(null);
    }
  };

  const copyPortalLink = async () => {
    try {
      const { data: d } = await axios.post(`${API}/sellers/${sellerId}/portal-link`, {}, { withCredentials: true });
      const link = `${window.location.origin}${d.path}`;
      await navigator.clipboard.writeText(link);
      toast.success('Portal link copied to clipboard');
    } catch (error) {
      toast.error('Failed to get portal link');
    }
  };

  const smsPortalLink = async () => {
    try {
      await axios.post(`${API}/sellers/${sellerId}/portal-link/send`, { base_url: window.location.origin }, { withCredentials: true });
      toast.success('Portal link sent to seller via SMS');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send portal link');
    }
  };

  const rateSeller = async (n) => {
    try {
      await axios.patch(`${API}/sellers/${sellerId}`, { rating: n }, { withCredentials: true });
      setSeller(prev => ({ ...prev, rating: n }));
      toast.success(`Seller rated ${n}/5`);
    } catch (error) {
      toast.error('Failed to save rating');
    }
  };

  useEffect(() => {
    loadSellerData();
  }, [loadSellerData]);

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${amount.toLocaleString()}`;
  };

  const formatRelativeTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(isoString);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <p className="text-[#474747]">Loading seller profile...</p>
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <p className="text-[#474747]">Seller not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E9E9E9]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#474747] mb-4"
          >
            <Home className="w-4 h-4" />
            Back to Dashboard
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#C6E76C] flex items-center justify-center">
                <User className="w-8 h-8 text-[#474747]" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-semibold text-[#474747]">{seller.full_name}</h1>
                <div className="flex items-center gap-4 mt-1">
                  {seller.phone && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      {seller.phone}
                    </div>
                  )}
                  {seller.email && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      {seller.email}
                    </div>
                  )}
                  {seller.suburb && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      {seller.suburb}, {seller.state}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-2" data-testid="seller-rating">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => rateSeller(n)} title={`Rate ${n}/5`} data-testid={`seller-rating-star-${n}`}>
                      <Star className={`w-5 h-5 transition-colors ${n <= (seller.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300 hover:text-amber-300'}`} />
                    </button>
                  ))}
                  <span className="text-xs text-gray-400 ml-1.5">
                    {seller.rating ? `${seller.rating}/5 internal rating` : 'Rate this seller (internal only)'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={copyPortalLink} data-testid="copy-portal-link-button">
                <Link2 className="w-4 h-4 mr-2" />
                Copy Portal Link
              </Button>
              <Button variant="outline" onClick={smsPortalLink} data-testid="sms-portal-link-button">
                <Send className="w-4 h-4 mr-2" />
                SMS Portal Link
              </Button>
              <Button className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold">
                <MessageSquare className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-[#E9E9E9] p-6">
            <p className="text-sm text-gray-500 font-medium">Lifetime Sales</p>
            <p className="text-3xl font-semibold text-[#474747] mt-2">{formatCurrency(stats?.lifetime_sales || 0)}</p>
            <p className="text-xs text-gray-500 mt-1">{stats?.total_sold || 0} items sold</p>
          </div>

          <div className="bg-white rounded-lg border border-[#E9E9E9] p-6">
            <p className="text-sm text-gray-500 font-medium">Total Payouts</p>
            <p className="text-3xl font-semibold text-[#474747] mt-2">{formatCurrency(stats?.lifetime_payouts || 0)}</p>
            <p className="text-xs text-gray-500 mt-1">70% of sales</p>
          </div>

          <div className="bg-white rounded-lg border border-[#E9E9E9] p-6">
            <p className="text-sm text-gray-500 font-medium">Active Sales</p>
            <p className="text-3xl font-semibold text-[#474747] mt-2">{stats?.active_items || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Currently live</p>
          </div>

          <div className="bg-white rounded-lg border border-[#E9E9E9] p-6">
            <p className="text-sm text-gray-500 font-medium">Applications</p>
            <p className="text-3xl font-semibold text-[#474747] mt-2">{stats?.active_applications || 0}</p>
            <p className="text-xs text-gray-500 mt-1">In progress</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'items', 'applications', 'commissions', 'activity', 'messages', 'notes', 'tasks'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-[#C6E76C] text-[#474747]'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl border border-[#E9E9E9]">
          {activeTab === 'overview' && (
            <div className="p-6">
              <h3 className="text-lg font-semibold text-[#474747] mb-4">Seller Overview</h3>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Contact Information</p>
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-[#474747]"><span className="font-medium">Phone:</span> {seller.phone || 'N/A'}</p>
                    <p className="text-sm text-[#474747]"><span className="font-medium">Email:</span> {seller.email || 'N/A'}</p>
                    <p className="text-sm text-[#474747]"><span className="font-medium">Address:</span> {seller.address_line_1 || 'N/A'}</p>
                    {seller.suburb && <p className="text-sm text-[#474747]">{seller.suburb}, {seller.state} {seller.postcode}</p>}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500 font-medium">Account Details</p>
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-[#474747]"><span className="font-medium">Member Since:</span> {formatDate(seller.created_at)}</p>
                    <p className="text-sm text-[#474747]"><span className="font-medium">Total Items:</span> {items.length}</p>
                    <p className="text-sm text-[#474747]"><span className="font-medium">Success Rate:</span> {stats?.total_sold > 0 ? `${Math.round((stats.total_sold / items.length) * 100)}%` : '0%'}</p>
                  </div>
                </div>
              </div>

              {seller.notes && (
                <div className="mt-6">
                  <p className="text-sm text-gray-500 font-medium">Notes</p>
                  <p className="text-sm text-[#474747] mt-2">{seller.notes}</p>
                </div>
              )}

              {agreements.length > 0 && (
                <div className="mt-6" data-testid="seller-agreements-section">
                  <p className="text-sm text-gray-500 font-medium mb-2">Signed Sales Agreements ({agreements.length})</p>
                  <div className="space-y-2">
                    {agreements.map(ag => (
                      <div key={ag.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border border-[#E9E9E9]">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-sm font-semibold text-[#474747]">
                              {ag.item_title || 'Item'} <span className="text-xs text-gray-400 font-normal">({ag.reference_code})</span>
                            </p>
                            <p className="text-xs text-gray-500">Signed {formatDate(ag.signed_at)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => window.open(`${API}/public/agreements/${ag.id}`, '_blank')} data-testid={`view-agreement-${ag.id}`}>
                            View
                          </Button>
                          <Button size="sm" variant="outline" disabled={sendingAgreementId === ag.id} onClick={() => sendSellerAgreement(ag, 'sms')} data-testid={`sms-agreement-${ag.id}`}>
                            SMS
                          </Button>
                          <Button size="sm" variant="outline" disabled={sendingAgreementId === ag.id} onClick={() => sendSellerAgreement(ag, 'email')} data-testid={`email-agreement-${ag.id}`}>
                            Email
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'items' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#474747]">Items ({items.length})</h3>
              </div>

              {items.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No items found</p>
              ) : (
                <div className="space-y-3">
                  {items.map(item => (
                    <div
                      key={item.id}
                      onClick={() => navigate(`/item/${item.id}`)}
                      className="flex items-center justify-between p-4 rounded-lg border border-[#E9E9E9] hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center">
                          <Package className="w-6 h-6 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#474747]">{item.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{item.category} • {item.lifecycle_stage}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-[#474747]">{formatCurrency(item.listed_price || item.sale_price)}</p>
                        <p className="text-xs text-gray-500">{formatDate(item.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'applications' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#474747]">Applications ({applications.length})</h3>
              </div>

              {applications.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No applications found</p>
              ) : (
                <div className="space-y-3">
                  {applications.map(app => (
                    <div
                      key={app.id}
                      onClick={() => navigate(`/application/${app.id}`)}
                      className="flex items-center justify-between p-4 rounded-lg border border-[#E9E9E9] hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div>
                        <p className="text-sm font-medium text-[#474747]">{app.reference_code}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{app.item_title} • {app.lifecycle_stage}</p>
                      </div>
                      <p className="text-xs text-gray-500">{formatDate(app.submitted_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'commissions' && (
            <div className="p-6" data-testid="commissions-tab">
              <h3 className="text-lg font-semibold text-[#474747] mb-4">Commission History</h3>
              {(!commissions || commissions.items.length === 0) ? (
                <p className="text-sm text-gray-500 text-center py-8">No sold items yet — commission history appears here once items sell</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 font-medium">Total Sales ({commissions.totals.count} items)</p>
                      <p className="text-2xl font-semibold text-[#474747] mt-1">${commissions.totals.total_sales.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div className="bg-[#F7FBEC] border border-[#C6E76C] rounded-lg p-4">
                      <p className="text-xs text-gray-500 font-medium">Seller Payouts (70%)</p>
                      <p className="text-2xl font-semibold text-[#3D4237] mt-1">${commissions.totals.total_payouts.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 font-medium">Listers Commission (30%)</p>
                      <p className="text-2xl font-semibold text-[#474747] mt-1">${commissions.totals.total_commission.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                      <thead className="border-b border-[#E9E9E9]">
                        <tr>
                          {['Item', 'Sold', 'Sale Price', 'Payout (70%)', 'Commission (30%)', 'Status'].map(h => (
                            <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E9E9E9]">
                        {commissions.items.map(it => (
                          <tr key={it.id} onClick={() => navigate(`/item/${it.id}`)} className="hover:bg-gray-50 cursor-pointer" data-testid={`commission-row-${it.id}`}>
                            <td className="px-3 py-2.5 text-sm font-medium text-[#474747]">{it.title}</td>
                            <td className="px-3 py-2.5 text-xs text-gray-500">{formatDate(it.sold_at)}</td>
                            <td className="px-3 py-2.5 text-sm font-semibold text-[#474747]">${Number(it.sale_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2.5 text-sm text-gray-700">${Number(it.payout_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2.5 text-sm text-gray-700">${Number(it.commission_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2.5">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${it.lifecycle_stage === 'payout_issued' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {it.lifecycle_stage === 'payout_issued' ? 'Paid out' : 'Payout pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="p-6">
              <h3 className="text-lg font-semibold text-[#474747] mb-4">Recent Activity</h3>

              {activity.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No activity</p>
              ) : (
                <div className="space-y-4">
                  {activity.map(act => (
                    <div key={act.id} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-[#C6E76C]"></div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[#474747]">{act.description}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{formatRelativeTime(act.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#474747]">Messages</h3>
                <Button size="sm" className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747]">
                  <Plus className="w-4 h-4 mr-2" />
                  New Message
                </Button>
              </div>

              <div className="space-y-4">
                {/* SMS */}
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">SMS ({messages.sms?.length || 0})</p>
                  {messages.sms && messages.sms.length > 0 ? (
                    <div className="space-y-2">
                      {messages.sms.slice(0, 5).map(msg => (
                        <div key={msg.id} className="p-3 rounded-lg border border-[#E9E9E9]">
                          <p className="text-sm text-[#474747]">{msg.message_body}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatRelativeTime(msg.sent_at)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No SMS messages</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Emails ({messages.emails?.length || 0})</p>
                  {messages.emails && messages.emails.length > 0 ? (
                    <div className="space-y-2">
                      {messages.emails.slice(0, 5).map(email => (
                        <div key={email.id} className="p-3 rounded-lg border border-[#E9E9E9]">
                          <p className="text-sm font-medium text-[#474747]">{email.subject}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatRelativeTime(email.sent_at)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No emails</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#474747]">Notes</h3>
                <Button size="sm" className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747]">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </div>
              <p className="text-sm text-gray-500 text-center py-8">Notes feature coming soon</p>
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#474747]">Tasks</h3>
                <Button size="sm" className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Task
                </Button>
              </div>
              <p className="text-sm text-gray-500 text-center py-8">Tasks feature coming soon</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
